import {Record, List, Map} from 'immutable';
import moment from 'moment-timezone';

import generateUUID from 'web-app/util/uuid';
import {hasValue} from 'web-app/util/typescript';
import SHARED_ENVIRONMENT_CONFIG from 'web-app/shared/environment';

const Types = {
    String: 'string',
    Money: 'Money',
    LocalDate: 'LocalDate',
    Markdown: 'markdown',
    Change: 'Change',
    LocalDateRange: 'LocalDateRange',
    Email: 'Email',
};

const Link = Record({
    entity: '',
    id: '',
    // extra: Map | null
    extra: null,
});

const CellItem = Record({
    key: null,
    appearance: Map(),
    label: '',
    style: '',
    labelType: 'string',
    labelData: null,
    subtitle: null,
    subtitleType: null,
    subtitleData: null,
    imageUrl: null,
    url: null,
    routeArgs: null,
    cellType: undefined,
    cellProps: null,
    sortable: false,
    sortValue: null,
    sortMethod: null,
    onClick: null,
    link: null,
    items: List(),
    subtitleItems: List(),
    CellComponent: null,
    nowrap: false,
    css: undefined,
    emphasized: false,
});

const Row = Record({
    id: '',
    parentId: '',
    startCollapsed: false,
    appearance: Map(),
    childRows: List(),
    cells: Map(),
    onClick: null,
    routeArgs: null,
    rowType: undefined,
    RowComponent: undefined,
    rowProps: undefined,
    meta: Map(),
});

const Table = Record({
    hideHeader: false,
    headers: List(),
    rows: List(),
    offset: 0,
    total: null,
});

const cellDataFromAPI = cellData => {
    if (!cellData) {
        return new CellItem();
    }

    // Backwards compatibility.
    if (typeof cellData === 'string') {
        return new CellItem({
            label: cellData,
        });
    }

    const subtitleData =
        cellData.subtitleType === Types.LocalDateRange
            ? {
                  ...cellData.subtitleData,
                  from: moment(cellData.subtitleData.from),
                  to: moment(cellData.subtitleData.to),
              }
            : cellData.subtitleData;

    const link = cellData.link
        ? new Link({
              ...cellData.link,
              extra: cellData.link.extra ? Map(cellData.link.extra) : null,
          })
        : null;

    if (cellData.labelType === Types.LocalDateRange) {
        return new CellItem({
            ...cellData,
            subtitleData,
            link,
            appearance: Map(cellData.appearance),
            labelData: {
                from: moment(cellData.labelData.from),
                to: moment(cellData.labelData.to),
            },
        });
    }

    return new CellItem({
        ...cellData,
        subtitleData,
        link,
        items: List(cellData.items).map(item => new CellItem(item)),
        appearance: Map(cellData.appearance),
    });
};

const rowCellsFromAPI = cells => {
    if (!cells) {
        return undefined;
    }
    return new Map(
        Object.keys(cells).reduce(
            (obj, key) => ({
                ...obj,
                [key]: cellDataFromAPI(cells[key]),
            }),
            {},
        ),
    );
};

const rowFromAPI = (row, parentId) => {
    if (!hasValue(row.id) && SHARED_ENVIRONMENT_CONFIG.IS_DEVELOPMENT) {
        console.warn(
            `You havent provided an id to the rows of a generic table.
            This will cause each row to re-mount on every render.
            Please consider adding an id`,
        );
    }
    const id = row.id || generateUUID();
    return new Row({
        ...row,
        id,
        parentId,
        appearance: Map(row.appearance),
        cells: rowCellsFromAPI(row.cells),
        childRows: List(row.childRows).map(row => rowFromAPI(row, id)),
        meta: Map(row.meta),
    });
};

function fromAPI(data) {
    const headerList = List(data.headers);

    const headers = headerList.map(h => new CellItem(h));
    const rows = List(data.rows).map(row => rowFromAPI(row, 'root'));

    return new Table({
        ...data,
        headers,
        rows,
    });
}

export default {
    record: Table,
    default: new Table(),
    CellItem,
    fromAPI,
    Types,
};
